import React from "react";
import clsx from "clsx";

// Container Types
export interface ContainerProps {
  children: React.ReactNode;
  maxWidth?: "sm" | "md" | "lg" | "xl" | "full";
  padding?: "none" | "sm" | "md" | "lg";
  className?: string;
}

// Stack Types
export interface StackProps {
  children: React.ReactNode;
  direction?: "row" | "col";
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16 | 20 | 24 | 32;
  align?: "start" | "center" | "end" | "between" | "around" | "evenly";
  justify?: "start" | "center" | "end" | "between" | "around" | "evenly";
  wrap?: boolean;
  divider?: boolean;
  className?: string;
}

// Grid Types
export interface ResponsiveMap {
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
  "2xl"?: number;
}

export interface GridProps {
  children: React.ReactNode;
  cols?: number | ResponsiveMap;
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16 | 20 | 24 | 32;
  autoFit?: boolean;
  autoFill?: boolean;
  className?: string;
}

// Divider Types
export interface DividerProps {
  direction?: "horizontal" | "vertical";
  label?: string;
  icon?: React.ReactNode;
  className?: string;
}

// Container Component
export const Container: React.FC<ContainerProps> = ({
  children,
  maxWidth = "lg",
  padding = "md",
  className,
}) => {
  const maxWidthClasses = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-4xl",
    xl: "max-w-6xl",
    full: "max-w-full",
  };

  const paddingClasses = {
    none: "",
    sm: "px-4 py-2",
    md: "px-6 py-4",
    lg: "px-8 py-6",
  };

  return (
    <div
      className={clsx(
        "mx-auto w-full",
        maxWidthClasses[maxWidth],
        paddingClasses[padding],
        className,
      )}
    >
      {children}
    </div>
  );
};

// Stack Component
export const Stack: React.FC<StackProps> = ({
  children,
  direction = "col",
  gap = 4,
  align = "start",
  justify = "start",
  wrap = false,
  divider = false,
  className,
}) => {
  const directionClasses = {
    row: "flex-row",
    col: "flex-col",
  };

  const alignClasses = {
    start: "items-start",
    center: "items-center",
    end: "items-end",
    between: "items-stretch",
    around: "items-stretch",
    evenly: "items-stretch",
  };

  const justifyClasses = {
    start: "justify-start",
    center: "justify-center",
    end: "justify-end",
    between: "justify-between",
    around: "justify-around",
    evenly: "justify-evenly",
  };

  const gapClasses = {
    0: "gap-0",
    1: "gap-1",
    2: "gap-2",
    3: "gap-3",
    4: "gap-4",
    5: "gap-5",
    6: "gap-6",
    8: "gap-8",
    10: "gap-10",
    12: "gap-12",
    16: "gap-16",
    20: "gap-20",
    24: "gap-24",
    32: "gap-32",
  };

  const childrenArray = React.Children.toArray(children);

  if (divider && childrenArray.length > 1) {
    const dividerElement = (
      <div className={clsx("border-gray-700", direction === "row" ? "border-l" : "border-t")} />
    );

    const childrenWithDividers = childrenArray.reduce<React.ReactNode[]>((acc, child, index) => {
      acc.push(child);
      if (index < childrenArray.length - 1) {
        acc.push(dividerElement);
      }
      return acc;
    }, []);

    return (
      <div
        className={clsx(
          "flex",
          directionClasses[direction],
          alignClasses[align],
          justifyClasses[justify],
          gapClasses[gap],
          {
            "flex-wrap": wrap,
          },
          className,
        )}
      >
        {childrenWithDividers}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "flex",
        directionClasses[direction],
        alignClasses[align],
        justifyClasses[justify],
        gapClasses[gap],
        {
          "flex-wrap": wrap,
        },
        className,
      )}
    >
      {children}
    </div>
  );
};

// Grid Component
export const Grid: React.FC<GridProps> = ({
  children,
  cols = 1,
  gap = 4,
  autoFit = false,
  autoFill = false,
  className,
}) => {
  const gapClasses = {
    0: "gap-0",
    1: "gap-1",
    2: "gap-2",
    3: "gap-3",
    4: "gap-4",
    5: "gap-5",
    6: "gap-6",
    8: "gap-8",
    10: "gap-10",
    12: "gap-12",
    16: "gap-16",
    20: "gap-20",
    24: "gap-24",
    32: "gap-32",
  };

  const getGridClasses = () => {
    if (autoFit) {
      return "grid-cols-[repeat(auto-fit,minmax(250px,1fr))]";
    }

    if (autoFill) {
      return "grid-cols-[repeat(auto-fill,minmax(200px,1fr))]";
    }

    if (typeof cols === "number") {
      const colClasses = {
        1: "grid-cols-1",
        2: "grid-cols-2",
        3: "grid-cols-3",
        4: "grid-cols-4",
        5: "grid-cols-5",
        6: "grid-cols-6",
        12: "grid-cols-12",
      };
      return colClasses[cols as keyof typeof colClasses] || `grid-cols-${cols}`;
    }

    // Responsive map
    const responsiveClasses = [];
    if (cols.sm) responsiveClasses.push(`sm:grid-cols-${cols.sm}`);
    if (cols.md) responsiveClasses.push(`md:grid-cols-${cols.md}`);
    if (cols.lg) responsiveClasses.push(`lg:grid-cols-${cols.lg}`);
    if (cols.xl) responsiveClasses.push(`xl:grid-cols-${cols.xl}`);
    if (cols["2xl"]) responsiveClasses.push(`2xl:grid-cols-${cols["2xl"]}`);

    return responsiveClasses.length > 0 ? responsiveClasses.join(" ") : "grid-cols-1";
  };

  return (
    <div className={clsx("grid", getGridClasses(), gapClasses[gap], className)}>{children}</div>
  );
};

// Divider Component
export const Divider: React.FC<DividerProps> = ({
  direction = "horizontal",
  label,
  icon,
  className,
}) => {
  if (direction === "vertical") {
    return <div className={clsx("w-px h-full bg-gray-700", className)} />;
  }

  return (
    <div className={clsx("relative", className)}>
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-gray-700" />
      </div>
      {(label || icon) && (
        <div className="relative flex justify-center text-sm">
          <span className="px-2 text-gray-400 flex items-center gap-2">
            {icon}
            {label}
          </span>
        </div>
      )}
    </div>
  );
};

// Specialized Layout Components

// Spacer Component
export interface SpacerProps {
  size?: 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16 | 20 | 24 | 32;
  direction?: "horizontal" | "vertical";
  className?: string;
}

export const Spacer: React.FC<SpacerProps> = ({ size = 4, direction = "vertical", className }) => {
  const sizeClasses = {
    1: direction === "vertical" ? "h-1" : "w-1",
    2: direction === "vertical" ? "h-2" : "w-2",
    3: direction === "vertical" ? "h-3" : "w-3",
    4: direction === "vertical" ? "h-4" : "w-4",
    5: direction === "vertical" ? "h-5" : "w-5",
    6: direction === "vertical" ? "h-6" : "w-6",
    8: direction === "vertical" ? "h-8" : "w-8",
    10: direction === "vertical" ? "h-10" : "w-10",
    12: direction === "vertical" ? "h-12" : "w-12",
    16: direction === "vertical" ? "h-16" : "w-16",
    20: direction === "vertical" ? "h-20" : "w-20",
    24: direction === "vertical" ? "h-24" : "w-24",
    32: direction === "vertical" ? "h-32" : "w-32",
  };

  return <div className={clsx(sizeClasses[size], className)} />;
};

// Center Component
export interface CenterProps {
  children: React.ReactNode;
  className?: string;
}

export const Center: React.FC<CenterProps> = ({ children, className }) => {
  return <div className={clsx("flex items-center justify-center", className)}>{children}</div>;
};

// AspectRatio Component
export interface AspectRatioProps {
  children: React.ReactNode;
  ratio?: "square" | "video" | "photo" | "wide" | number;
  className?: string;
}

export const AspectRatio: React.FC<AspectRatioProps> = ({
  children,
  ratio = "square",
  className,
}) => {
  const ratioClasses = {
    square: "aspect-square",
    video: "aspect-video",
    photo: "aspect-[4/3]",
    wide: "aspect-[16/9]",
  };

  const getRatioClass = () => {
    if (typeof ratio === "number") {
      return `aspect-[${ratio}]`;
    }
    return ratioClasses[ratio];
  };

  return (
    <div className={clsx("relative overflow-hidden", getRatioClass(), className)}>{children}</div>
  );
};

// Flex Component (simplified Stack with more control)
export interface FlexProps {
  children: React.ReactNode;
  direction?: "row" | "col" | "row-reverse" | "col-reverse";
  wrap?: boolean | "wrap" | "wrap-reverse" | "nowrap";
  align?: "start" | "center" | "end" | "stretch" | "baseline";
  justify?: "start" | "center" | "end" | "between" | "around" | "evenly";
  gap?: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 8 | 10 | 12 | 16 | 20 | 24 | 32;
  className?: string;
}

export const Flex: React.FC<FlexProps> = ({
  children,
  direction = "row",
  wrap = false,
  align = "start",
  justify = "start",
  gap = 0,
  className,
}) => {
  const directionClasses = {
    row: "flex-row",
    col: "flex-col",
    "row-reverse": "flex-row-reverse",
    "col-reverse": "flex-col-reverse",
  };

  const getWrapClass = (wrap: boolean | "wrap" | "wrap-reverse" | "nowrap"): string => {
    if (wrap === true) return "flex-wrap";
    if (wrap === false) return "flex-nowrap";
    if (wrap === "wrap") return "flex-wrap";
    if (wrap === "wrap-reverse") return "flex-wrap-reverse";
    if (wrap === "nowrap") return "flex-nowrap";
    return "flex-nowrap";
  };

  const alignClasses = {
    start: "items-start",
    center: "items-center",
    end: "items-end",
    stretch: "items-stretch",
    baseline: "items-baseline",
  };

  const justifyClasses = {
    start: "justify-start",
    center: "justify-center",
    end: "justify-end",
    between: "justify-between",
    around: "justify-around",
    evenly: "justify-evenly",
  };

  const gapClasses = {
    0: "gap-0",
    1: "gap-1",
    2: "gap-2",
    3: "gap-3",
    4: "gap-4",
    5: "gap-5",
    6: "gap-6",
    8: "gap-8",
    10: "gap-10",
    12: "gap-12",
    16: "gap-16",
    20: "gap-20",
    24: "gap-24",
    32: "gap-32",
  };

  return (
    <div
      className={clsx(
        "flex",
        directionClasses[direction],
        getWrapClass(wrap),
        alignClasses[align],
        justifyClasses[justify],
        gapClasses[gap],
        className,
      )}
    >
      {children}
    </div>
  );
};
